import React, { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import MfaVerificationModal from './MfaVerificationModal'
import { supabase } from '../../lib/supabase'
import authAPI from '../../lib/authAPI'
import toast from 'react-hot-toast'

const Login = ({ onToggleMode, onForgotPassword }) => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [mfaRequired, setMfaRequired] = useState(false)
  const [mfaData, setMfaData] = useState(null)
  const [mfaLoading, setMfaLoading] = useState(false)
  const { signIn, signInWithMfa } = useAuth()


  // CRITICAL: Sync session tokens between Supabase client and authAPI after direct login
  const syncSessionToAuthAPI = async (session) => {
    try {
      console.log('🔄 Syncing session from Supabase client to authAPI...')
      console.log('🔄 Session data:', {
        hasAccessToken: !!session?.access_token,
        hasRefreshToken: !!session?.refresh_token,
        tokenLength: session?.access_token?.length || 0
      })
      
      if (session?.access_token && session?.refresh_token) {
        // Store tokens in authAPI system for Edge Function calls
        authAPI.saveTokensToStorage(session.access_token, session.refresh_token)
        
        console.log('🔄 Session sync successful - authAPI now has tokens for Edge Functions')
        // Test the session sync by making a quick API call
        try {
          console.log('🧪 Testing Edge Function access after session sync...')
          const testResponse = await fetch('https://vracotqahobdmgywcqow.supabase.co/functions/v1/user-module-access', {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'apikey': authAPI.supabasePublishableKey,
              'Authorization': `Bearer ${authAPI.token}`
            }
          })
          console.log('🧪 Test API call response status:', testResponse.status)
          
          if (testResponse.status === 200) {
            console.log('🧪 ✅ Session sync successful - API calls working!')
          } else {
            console.log('🧪 ❌ Session sync incomplete - API still failing:', await testResponse.text())
          }
        } catch (testError) {
          console.log('🧪 ❌ Session sync test failed:', testError.message)
        }
        
        return true
      } else {
        console.error('🔄 Session sync failed - missing tokens', {
          session: session ? 'exists' : 'null',
          accessToken: session?.access_token ? 'exists' : 'missing',
          refreshToken: session?.refresh_token ? 'exists' : 'missing'
        })
        return false
      }
    } catch (error) {
      console.error('🔄 Session sync error:', error)
      return false
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!email || !password) {
      toast.error('Please fill in all fields')
      return
    }

    setLoading(true)
    
    try {
      // Use official Supabase signInWithPassword for initial login
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })
      
      if (error) {
        toast.error(error.message)
        return
      }

      // Get session and immediately sync tokens for API access
      const session = data.session
      console.log('🔐 Login successful, syncing session tokens immediately...')
      
      // CRITICAL: Sync session to authAPI FIRST before any other operations
      // This ensures tokens are available when AuthContext triggers useModuleAccess
      const syncSuccess = await syncSessionToAuthAPI(session)
      if (!syncSuccess) {
        console.error('🔐 Session sync failed - API calls may fail')
      }

      // Check if user has MFA factors and current AAL level
      const { data: factors } = await supabase.auth.mfa.listFactors()
      const currentAAL = session?.aal || 'aal1'
      
      console.log('🔐 Login status:', {
        user: session?.user?.email,
        aal: currentAAL,
        hasTotpFactors: factors?.totp?.length > 0,
        tokensSync: syncSuccess
      })
      
      // If user has TOTP factors but is only AAL1, prompt for MFA upgrade
      if (factors?.totp && factors.totp.length > 0 && currentAAL === 'aal1') {
        // User has MFA enrolled but needs to verify to upgrade to AAL2
        setMfaData({ 
          factors: factors.totp,
          session: session,
          needsUpgrade: true 
        })
        setMfaRequired(true)
      } else {
        // User successfully logged in (either no MFA or already AAL2)
        console.log('🔐 User login complete - no MFA needed or already AAL2')
        toast.success('Welcome back!')
      }
    } catch (error) {
      console.error('Login error:', error)
      toast.error('An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  const handleMfaVerify = async (code) => {
    setMfaLoading(true)
    
    try {
      // Use the first available TOTP factor for MFA verification
      const factor = mfaData.factors[0]
      
      // Create MFA challenge
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: factor.id
      })
      
      if (challengeError) {
        toast.error('Failed to create MFA challenge')
        return
      }

      // Verify the TOTP code
      const { data: verifyData, error: verifyError } = await supabase.auth.mfa.verify({
        factorId: factor.id,
        challengeId: challengeData.id,
        code: code
      })
      
      if (verifyError) {
        toast.error('Invalid verification code. Please try again.')
        return
      }
      
      // Get the upgraded session after MFA verification
      const { data: { session: upgradedSession } } = await supabase.auth.getSession()
      
      // Successfully upgraded to AAL2
      console.log('🔐 MFA verification successful - session upgraded to AAL2')
      
      // CRITICAL: Sync upgraded session to authAPI for Edge Function access
      const syncSuccess = await syncSessionToAuthAPI(upgradedSession)
      if (!syncSuccess) {
        console.error('🔐 MFA session sync failed - API calls may fail')
      }
      
      toast.success('Welcome back!')
      setMfaRequired(false)
      setMfaData(null)
    } catch (error) {
      console.error('🔐 MFA verification error:', error)
      toast.error('Verification failed. Please try again.')
    } finally {
      setMfaLoading(false)
    }
  }

  const handleMfaCancel = () => {
    setMfaRequired(false)
    setMfaData(null)
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          {/* E8 Tracker Logo */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <img 
                src="/E8T-Full-Transparent.png" 
                alt="Essential 8 Tracker" 
                className="h-28 mx-auto drop-shadow-lg"
                style={{ maxWidth: '420px', height: 'auto' }}
              />
              {/* Subtle glow effect */}
              <div className="absolute inset-0 rounded-lg bg-blue-500 opacity-10 blur-xl"></div>
            </div>
          </div>
          
          <h2 className="text-3xl font-extrabold text-gray-900 mb-2">
            Sign In
          </h2>
          <p className="text-sm text-gray-600">
            Access your cybersecurity assessment dashboard
          </p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter your password"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="text-center space-y-2">
              <button
                onClick={onForgotPassword}
                className="text-sm text-gray-600 hover:text-gray-500"
              >
                Forgot your password?
              </button>
              <div>
                <button
                  onClick={onToggleMode}
                  className="text-sm text-blue-600 hover:text-blue-500"
                >
                  Don't have an account? Sign up
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MFA Verification Modal */}
      {mfaRequired && (
        <MfaVerificationModal
          email={email}
          onVerify={handleMfaVerify}
          onCancel={handleMfaCancel}
          loading={mfaLoading}
        />
      )}
    </div>
  )
}

export default Login
